import type { HealthCheckFinding } from '../models/healthCheck.types'
import { toFindings } from '../service/mapFinding'

import type {
  HealthCheckClock,
  HealthCheckLoadApi,
  HealthCheckLoadStore,
  HealthCheckScopeGetters,
} from './healthCheckFlow.types'

import { ApiError } from '@/shared/http'

export interface LoadHealthChecksFlowDeps {
  store: HealthCheckLoadStore
  api: HealthCheckLoadApi
  getters: HealthCheckScopeGetters
  clock: HealthCheckClock
  /** Findings this viewer may see; the Meta checkpoint card is sysadmin-only (ORP-26183). */
  include?: (finding: HealthCheckFinding) => boolean
}

export class LoadHealthChecksFlow {
  constructor(private readonly deps: LoadHealthChecksFlowDeps) {}

  async execute(): Promise<void> {
    const accountId = this.deps.getters.accountId()
    const workspaceId = this.deps.getters.workspaceId()
    if (accountId == null || workspaceId == null) return

    this.deps.store.setLoading()
    try {
      const dtos = await this.deps.api.list(accountId, workspaceId)
      if (!this.isCurrentScope(accountId, workspaceId)) return
      const findings = toFindings(dtos, this.deps.clock.now())
      const include = this.deps.include
      this.deps.store.setFindings(include ? findings.filter(include) : findings)
    } catch (cause) {
      if (!this.isCurrentScope(accountId, workspaceId)) return
      const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (error.kind !== 'canceled') this.deps.store.setError(error)
    }
  }

  private isCurrentScope(accountId: number, workspaceId: number): boolean {
    return (
      this.deps.getters.accountId() === accountId && this.deps.getters.workspaceId() === workspaceId
    )
  }
}
